<template>
  <div class="flex-row">
    <compare-tree :filter-options="treeFilterOptions" />

    <div
      style="width: 100%; max-width: calc(100% - 350px); height: 100%"
      class="pr-2"
    >
      <node-compare-form v-model="settings" />

      <chart
        :key="JSON.stringify(widgetConfig)"
        :widget="widgetConfig"
        :tags="tags"
        :aggregationSettingsInput="chartAggregationSettings"
        :dateRangeInput="chartDateRange"
        not-template
        use-tag-name-as-series-name
        style="width: 100%; height: 400px; min-height: 400px; max-height: 500px"
        @updated-chart-data="updateTable"
        @update-aggregationSettings="(val) => (chartAggregationSettings = val)"
        @update-dateRange="(val) => (chartDateRange = val)"
      />

      <div class="py-2">
        <so-data-table
          :title="$t('nodeCompare.dataTable.title')"
          :headers="dataTableConfig.headers"
          :data="dataTableConfig.data"
          :number-precision="3"
        />
      </div>
    </div>
  </div>
</template>
<script>
// The charts key is the widgetConfig stringified =>
// chart will be recreated every time a sensor is added or removed (Needed to resetup the series etc. when it's dynamically many).
import { mapState, mapActions, mapMutations } from "vuex";
import ModuleMeta from "@/_helpers/ModuleMeta";
import CompareTree from "./CompareTree.vue";
import NodeCompareForm from "./NodeCompareForm.vue";
import Chart from "@/components/db/widgets/Chart.vue";
import CsharpEnum from "@/_helpers/CsharpEnum";
import SoDataTable from "../common/SoDataTable.vue";
import { cloneDeep } from "lodash-es";
import {
  constructChartSeriesHeaderForTable,
  constructChartSeriesDataForTable,
} from "@/_helpers/widgetHelper";

export default {
  name: "Compare",

  components: {
    CompareTree,
    NodeCompareForm,
    Chart,
    SoDataTable,
  },

  props: {
    compareSensors: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    ...mapState("compare", ["selectedNodes"]),

    settings: {
      get() {
        return {
          chartAggregationSettings: this.chartAggregationSettings,
          chartDateRange: this.chartDateRange,
          treeFilterOptions: this.treeFilterOptions,
          nodes: this.selectedNodes,
        };
      },
      set(val) {
        if (val.chartAggregationSettings)
          this.chartAggregationSettings = val.chartAggregationSettings;

        if (val.chartDateRange) this.chartDateRange = val.chartDateRange;

        if (val.nodes) this.SET_UNFILTERED_SELECTED_NODES(val.nodes);

        if (val.treeFilterOptions)
          this.treeFilterOptions = val.treeFilterOptions;

        this.firstUpdateSinceLoaded = true;
      },
    },
  },

  data() {
    return {
      tags: [],
      treeFilterOptions: { applications: [], nodeTypes: [], decoders: [] },
      widgetConfig: {
        widgetType: CsharpEnum.ModuleType.MULTICHART,
        slots: [],
        meta: [
          {
            key: ModuleMeta.Enum.DEFAULT_TIMESPAN,
            value: "24_hour",
          },
        ],
      },
      chartAggregationSettings: {},
      chartDateRange: { start: "-24h", end: "Now" },
      dataTableConfig: {
        headers: [],
        data: [],
      },
      firstUpdateSinceViewLoaded: false,
    };
  },

  methods: {
    ...mapActions("tag", ["getTagWithMetaAndKeys"]),
    ...mapMutations("compare", ["SET_UNFILTERED_SELECTED_NODES"]),

    updateTable(series) {
      this.dataTableConfig.headers = constructChartSeriesHeaderForTable(
        series,
        true
      );
      this.dataTableConfig.data = constructChartSeriesDataForTable(series);
    },
  },

  created() {
    this.SET_UNFILTERED_SELECTED_NODES(cloneDeep(this.compareSensors));

    if (this.selectedNodes !== undefined)
      this.widgetConfig.slots = this.selectedNodes.map((node) => ({
        slotId: this.widgetConfig.slots.length,
        index: this.widgetConfig.slots.length + 1,
        key: node.key,
      }));
  },

  watch: {
    compareSensors: {
      handler(newVal) {
        this.SET_UNFILTERED_SELECTED_NODES(cloneDeep(newVal));

        // treeFilterOptions is set to trigger watcher in CompareTree to correctly expand and show the new keys in compareSensors
        this.treeFilterOptions = {
          applications: [],
          nodeTypes: [],
          decoders: [],
        };
      },
    },

    // On each change of the selected nodes we rebuild the chart configuration
    // to display the changes to the data
    selectedNodes: {
      async handler(newVal) {
        // Fetch meta to store for Chart
        await this.getTagWithMetaAndKeys(
          this.selectedNodes.map((d) => ({ deveui: d.deveui, key: d.key }))
        );

        this.tags = newVal
          .map((node) => node.deveui)
          .filter((v, i, a) => a.indexOf(v) === i); // Distinct

        let slots = [];
        let psm = [];
        const aggregation = {};
        for (var tag of this.tags) {
          let i = this.tags.indexOf(tag);

          for (var k of newVal.filter((n) => n.deveui === tag)) {
            const prevSlotId = this.widgetConfig.slots.find(
              (x) => x.deveui == k.deveui && x.key == k.key
            )?.slotId;
            if (
              prevSlotId !== undefined &&
              this.chartAggregationSettings[prevSlotId]
            )
              aggregation[slots.length] =
                this.chartAggregationSettings[prevSlotId];

            psm.push({
              slotId: slots.length,
              key: ModuleMeta.Enum.CHART_SMOOTH,
              value: true,
            });

            psm.push({
              slotId: slots.length,
              key: ModuleMeta.Enum.HIDE_BULLETS,
              value: true,
            });

            slots.push({
              slotId: slots.length,
              index: i + 1,
              deveui: k.deveui,
              key: k.key,
            });
          }
        }

        if (!this.firstUpdateSinceLoaded)
          this.chartAggregationSettings = aggregation;
        this.firstUpdateSinceLoaded = false;

        this.widgetConfig = {
          widgetType: CsharpEnum.ModuleType.MULTICHART,
          slots: slots,
          meta: [
            {
              key: ModuleMeta.Enum.DEFAULT_TIMESPAN,
              value: "24_hour",
            },
            ...psm,
          ],
        };
      },
      deep: true,
    },
  },
};
</script>

<style>
.flex-row {
  display: flex;
  flex-wrap: nowrap;
  height: 100%;
}
</style>
